import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { confirmNewsletterFn } from "@/lib/newsletter.functions";
import { z } from "zod";

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/uutiskirje/vahvista")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Vahvista uutiskirjeen tilaus | Montessori24" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ConfirmPage,
});

function ConfirmPage() {
  const { token } = useSearch({ from: "/uutiskirje/vahvista" });
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; already: boolean; discountCode?: string; guideUrl?: string }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "Vahvistuslinkistä puuttuu tunniste." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await confirmNewsletterFn({ data: { token } });
        if (cancelled) return;
        if (res.ok)
          setState({
            kind: "ok",
            already: res.alreadyConfirmed,
            discountCode: res.discountCode,
            guideUrl: res.guideUrl,
          });
        else setState({ kind: "error", message: res.error });
      } catch {
        if (!cancelled) setState({ kind: "error", message: "Vahvistus epäonnistui." });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Layout>
      <section className="container-prose py-24 md:py-32">
        <div className="mx-auto max-w-xl text-center">
          {state.kind === "loading" && (
            <p className="text-muted-foreground">Vahvistetaan tilaustasi…</p>
          )}
          {state.kind === "ok" && (
            <>
              <h1 className="font-display text-4xl text-foreground md:text-5xl">
                {state.already ? "Olet jo listalla" : "Tilaus vahvistettu"}
              </h1>
              <p className="mt-4 text-muted-foreground">
                Kiitos! Tässä lupaamamme lahjat – lähetimme ne myös sähköpostiisi.
              </p>
              {state.discountCode && (
                <div className="mx-auto mt-8 max-w-md rounded-2xl border border-border bg-cream p-6 text-left">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    10 % alennuskoodi ensitilaukseen
                  </p>
                  <p className="mt-2 font-display text-3xl tracking-widest text-clay">
                    {state.discountCode}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Syötä koodi kassalla saadaksesi alennuksen.
                  </p>
                </div>
              )}
              {state.guideUrl && (
                <a
                  href={state.guideUrl}
                  className="mt-6 inline-flex rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground hover:bg-cream"
                  download
                >
                  Lataa aktiviteettiopas (PDF)
                </a>
              )}
              <Link
                to="/kauppa"
                className="mt-4 ml-0 md:ml-3 inline-flex rounded-full bg-clay px-6 py-3 text-sm font-medium text-white"
              >
                Katso mallisto
              </Link>
            </>
          )}
          {state.kind === "error" && (
            <>
              <h1 className="font-display text-4xl text-foreground">Vahvistus epäonnistui</h1>
              <p className="mt-4 text-muted-foreground">{state.message}</p>
              <Link
                to="/"
                className="mt-8 inline-flex rounded-full border border-border px-6 py-3 text-sm"
              >
                Palaa etusivulle
              </Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}